import { Step } from "./states";
import { formatTransmissionTime } from "@utils/time";
import { buildISOMessage } from "@engine/iso8583/isoBuilder";
import { TransactionState } from "@models/transaction";

export type { TransactionState };

export type TransitionResult = {
  state: TransactionState;
  log: {
    step: Step;
    timestamp: number;
    input: any;
    output: any;
    explanation: string;
  };
};

function log(step: Step, input: any, output: any, explanation: string) {
  return {
    step,
    timestamp: Date.now(),
    input,
    output,
    explanation
  };
}

// --- TRANSITIONS ---

export function handleCardCaptured(state: TransactionState): TransitionResult {
  const rawCardData = {
    pan: "4111111111111111",
    expiry: "12/28",
    cvv: "123"
  };

  return {
    state: {
      ...state,
      rawCardData
    },
    log: log(
      "CARD_CAPTURED",
      null,
      rawCardData,
      "Captured card details including PAN, expiry and CVV"
    )
  };
}

export function handleDataStructured(state: TransactionState): TransitionResult {
  const structuredData = {
    transaction_type: "PURCHASE",
    amount: (parseFloat(state.amount || "120.00") || 0).toFixed(2),
    currency_code: "USD",
    card_data: {
      pan: "4111111111111111",
      expiry: "281231",
      cardholder_name: "AGARWAL/ARNAV",
      track2_data: "4111111111111111D2812201000000000000"
    },
    emv_data: {
      arqc: "8A2C9F81B24E7D6A",
      atc: "004A",
      unpredictable_number: "7E3A9D1C",
      tvr: "0000000000"
    },
    pos_entry_mode: "05",
    terminal_id: "POS-8821A"
  };

  return {
    state: {
      ...state,
      structuredData
    },
    log: log(
      "DATA_STRUCTURED",
      state.rawCardData,
      structuredData,
      "Structured raw card data into transaction format"
    )
  };
}

export function handleISO(state: TransactionState): TransitionResult {
  // Concatenate EMV tags into a single hex string for Field 55
  const emvDataStr = state.structuredData!.emv_data 
    ? Object.values(state.structuredData!.emv_data).join("") 
    : undefined;

  const isoMessage = buildISOMessage({
    pan: state.structuredData!.card_data.pan,
    amount: parseFloat(state.structuredData!.amount), // string to number
    transmissionTime: formatTransmissionTime(),
    terminalId: state.structuredData!.terminal_id,
    processingCode: "000000",
    expiry: state.structuredData!.card_data.expiry,
    posEntryMode: state.structuredData!.pos_entry_mode + "1", // usually 3 digits
    track2: state.structuredData!.card_data.track2_data,
    currencyCode: state.structuredData!.currency_code,
    emvData: emvDataStr
  });

  return {
    state: {
      ...state,
      isoMessage
    },
    log: log(
      "ISO_BUILT",
      state.structuredData,
      isoMessage,
      "Constructed ISO 8583 message"
    )
  };
}

export function handleEncryption(state: TransactionState): TransitionResult {
  const raw = JSON.stringify(state.isoMessage);

  const encrypted = btoa(
    raw
      .split("")
      .map((c, i) =>
        String.fromCharCode(c.charCodeAt(0) ^ (i % 255))
      )
      .join("")
  );

  return {
    state: {
      ...state,
      encryptedPayload: encrypted
    },
    log: log(
      "ENCRYPTED",
      raw,
      encrypted,
      "Applied pseudo encryption to ISO message"
    )
  };
}

export function handleRouting(state: TransactionState): TransitionResult {
  const routingTrace = {
    hop_1: {
      node: "Acquirer Gateway",
      action: "Appends Forwarding Institution ID & ARN",
      trace_data: {
        fwd_id: "400000",
        acquirer_reference_number: "239019283912"
      }
    },
    hop_2: {
      node: "Payment Network Switch (e.g. VisaNet)",
      action: "BIN Routing & Risk Scoring",
      trace_data: {
        network_reference_number: "000000129482",
        stip_flag: "0",
        fraud_risk_score: "02"
      }
    },
    hop_3: {
      node: "Issuer Authorizer",
      action: "Payload Decryption & Evaluation",
      destination_bin: state.structuredData?.card_data?.pan?.substring(0, 6) || "411111"
    }
  };

  return {
    state: {
      ...state,
      routingTrace
    },
    log: log(
      "ROUTED",
      state.encryptedPayload,
      routingTrace,
      "Simulated routing through payment network and added trace metadata"
    )
  };
}

export function handleAuthorization(state: TransactionState): TransitionResult {
  // Simulate the Issuer's decision logic
  const amount = parseFloat(state.structuredData?.amount || "0");
  const fraudScore = parseInt(state.routingTrace?.hop_2?.trace_data?.fraud_risk_score || "00", 10);
  
  // Basic rule engine logic
  let status: "APPROVED" | "DECLINED" = "APPROVED";
  let responseCode = "00";
  let authId = "APP" + Math.floor(Math.random() * 90000 + 10000);
  let reasons: string[] = [];

  if (amount > 50000) {
    status = "DECLINED";
    responseCode = "51";
    reasons.push("Exceeds card limit");
    authId = "";
  } else if (fraudScore > 85) {
    status = "DECLINED";
    responseCode = "59";
    reasons.push("High fraud probability");
    authId = "";
  }

  const decision = {
    status,
    response_code: responseCode,
    authorization_id: authId,
    reasons,
    evaluation_time_ms: Math.floor(Math.random() * 40 + 10)
  };

  // Generate the 0110 Response Payload that goes back through the network
  const authResponsePayload = {
    mti: "0110",
    fields: {
      38: authId,
      39: responseCode,
      41: state.structuredData?.terminal_id || "TERM001"
    }
  };

  return {
    state: {
      ...state,
      decision,
      authResponsePayload
    },
    log: log(
      "AUTHORIZED",
      state.routingTrace,
      decision,
      "Issuer evaluated transaction and generated 0110 Response"
    )
  };
}